import type { DailyLandingInventory } from "@workspace/api-zod";
import { decodeLandingSeed } from "./compact-landing-seed.mjs";
import { getPlatformConfig, type Platform } from "@/lib/platform/config";

export type LandingInventoryBrand = DailyLandingInventory["brand"];

/** Public host and SSR scope win over the independently switchable display brand. */
export function landingInventoryBrandForPlatform(platform: Platform): LandingInventoryBrand {
  if (typeof window !== "undefined") {
    const host = window.location.hostname.toLowerCase().replace(/^www\./, "");
    for (const brand of ["driveshare", "bookaclassic"] as const) {
      const domain = getPlatformConfig(brand === "driveshare" ? "driveshare" : "bac")
        .domain.toLowerCase().replace(/^www\./, "");
      if (host === domain || host.endsWith(`.${domain}`)) return brand;
    }
  }
  if (typeof document !== "undefined") {
    try {
      const seed = decodeLandingSeed(JSON.parse(document.getElementById("landing-prerender-data")?.textContent || "null"));
      if (seed?.inventoryBrand === "driveshare" || seed?.inventoryBrand === "bookaclassic") {
        return seed.inventoryBrand;
      }
    } catch { /* No valid SSR seed: use the configured platform below. */ }
  }
  return platform === "driveshare" ? "driveshare" : "bookaclassic";
}

export function defaultLandingInventoryCountry(_brand: LandingInventoryBrand): string {
  // A missing visitor-country signal is not a display-brand signal. Keep
  // the legacy unscoped default stable across both public brands.
  return "GB";
}

export function normalizeLandingInventoryCountry(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(normalized) ? normalized : undefined;
}

export function selectLandingInventoryCountry(
  brand: LandingInventoryBrand,
  options: {
    configuredCountryCode?: unknown;
    selectedCountryCode?: unknown;
    seededCountryCode?: unknown;
  } = {},
): string {
  return normalizeLandingInventoryCountry(options.selectedCountryCode) ||
    normalizeLandingInventoryCountry(options.seededCountryCode) ||
    normalizeLandingInventoryCountry(options.configuredCountryCode) ||
    defaultLandingInventoryCountry(brand);
}